import React, {FC, MouseEvent, useState} from "react";
import {IconButton, Tooltip} from "@material-ui/core";
import CheckCircleOutlinedIcon from "@material-ui/icons/CheckCircleOutlined";
import CopyIcon from "view/images/CopyIcon";
import {useCopyTaskFileLink} from "hooks/file";

type CopyFileLinkButtonProps = {
  /** `files.id`. Absent for a not-yet-uploaded (local) attachment. */
  fileId?: string;
  /** The baked `files.url` — what lands in the clipboard. */
  url?: string;
  className?: string;
};

/**
 * Copy-link affordance for a task file chip. Publishing + clipboard live in
 * `useCopyTaskFileLink`; this is the icon around it.
 *
 * Renders nothing without a url, outside a `TaskFileShareProvider` (the public
 * grid preview mounts the same chips), or — in `publish` mode only — without a
 * file id to publish.
 */
const CopyFileLinkButton: FC<CopyFileLinkButtonProps> = ({
  fileId,
  url,
  className,
}) => {
  const {
    copyFileLink,
    isLoading,
    canShare,
    needsFileId,
  } = useCopyTaskFileLink();
  const [copied, setCopied] = useState(false);

  // `blob:` — a locally previewed file the user just picked; it has no `files`
  // row yet, so there is nothing to publish or link to.
  if (!canShare || !url || url.startsWith("blob:")) {
    return null;
  }
  if (needsFileId && !fileId) {
    return null;
  }

  const onClick = async (event: MouseEvent<HTMLElement>) => {
    // The chip itself opens the file on click — copying must not also
    // navigate.
    event.stopPropagation();
    const isCopied = await copyFileLink({fileId, url});
    if (!isCopied) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <Tooltip title="Copy permanent file link">
      {/* Tooltip needs a non-disabled child to hang events on while the
          request is in flight. */}
      <span style={{display: "inline-flex"}}>
        <IconButton
          className={className}
          onClick={onClick}
          disabled={isLoading || copied}
        >
          {copied ? <CheckCircleOutlinedIcon color="primary" /> : <CopyIcon />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default CopyFileLinkButton;
